import 'react-dots-loader/index.css'
import { kebabCase } from 'lodash'
import React from 'react'
import Loader from 'react-dots-loader'
import { Link, type LinkProps } from 'react-router-dom'

import theme from 'theme'
import { omit } from 'utils/obj'

export const styles = {
  height: theme.space[6]
}

function setColor(color) {
  return {
    backgroundColor: theme.colors[color][0],
    color: 'white',
    '&:hover, &:focus': {
      backgroundColor: theme.colors[color][1]
    },
    '&:disabled': {
      backgroundColor: theme.colors[color][1],
      opacity: theme.opacity[0]
    }
  }
}

const BUTTON_TYPES = {
  primary: {},
  additive: setColor('green'),
  secondary: {
    backgroundColor: theme.colors.white[0],
    color: theme.colors.steel[1],
    borderColor: theme.colors.gray[1],

    '.react-dots-loader > *': {
      backgroundColor: theme.colors.gray[1]
    },

    '&:hover, &:focus': {
      backgroundColor: theme.colors.white[0],
      color: theme.colors.blue[0]
    },

    '&:disabled': {
      backgroundColor: theme.colors.gray[0],
      opacity: theme.opacity[0],
      color: theme.colors.steel[1]
    }
  },
  transparent: {
    backgroundColor: 'transparent',
    color: theme.colors.white[0],
    borderColor: theme.colors.gray[1],

    '&:hover, &:focus': {
      backgroundColor: 'transparent'
    }
  },
  destructive: setColor('red'),
  get action() {
    return this.secondary
  }
}

export function getAriaLabel(
  children: React.ReactNode[] | React.ReactNode
): string {
  if (typeof children === 'string') {
    return children.trim()
  }

  if (Array.isArray(children)) {
    if (children.some((c) => typeof c === 'string')) {
      return children
        .filter((c) => typeof c === 'string')
        .map((c) => c.trim())
        .join(' ')
    }
  }

  return ''
}

export type ButtonProps = {
  label?: string
  children?: React.ReactNode | string
  type?: string
  title?: string
  loading?: boolean
  disabled?: boolean
  stretch?: boolean
  primary?: boolean
  creative?: boolean // @NOTE Keeping this here for some legacy ways we're handling "button type" in confirmation modal
  additive?: boolean
  secondary?: boolean
  transparent?: boolean
  destructive?: boolean
  iconOnly?: boolean
}

const getButtonStyles = ({
  stretch,
  iconOnly,
  ...rest
}: {
  stretch?: boolean
  iconOnly?: boolean
}) => [
  {
    '&:not(:disabled)': {
      cursor: 'pointer'
    },
    '.react-dots-loader > *': {
      backgroundColor: 'white'
    },
    textTransform: 'none',
    backgroundColor: theme.colors.blue[0],
    borderRadius: theme.borderRadius[2],
    border: 'solid 1px',
    borderColor: 'transparent',
    fontSize: theme.fontSizes[1],
    fontWeight: theme.fontWeights[1],
    lineHeight: 1,
    color: theme.colors.white[0],
    height: styles.height,
    width: iconOnly ? styles.height : null,
    paddingRight: iconOnly ? 0 : theme.space[3],
    paddingLeft: iconOnly ? 0 : theme.space[3],
    justifyContent: iconOnly ? 'center' : null,
    transition: 'background-color 0.15s linear',

    '&:hover, &:focus': {
      backgroundColor: theme.colors.blue[1],
      color: theme.colors.white[0]
    },

    '&:disabled': {
      backgroundColor: theme.colors.blue[0],
      cursor: 'not-allowed',
      opacity: theme.opacity[0]
    },

    '&:not(button)': {
      display: 'inline-flex',
      alignItems: 'center'
    }
  },
  ...Object.keys(BUTTON_TYPES)
    .filter((t) => rest[t])
    .map((t) => BUTTON_TYPES[t]),
  stretch && {
    width: '100%'
  }
]

const getButtonProps = (props, ref) => {
  const { loading, children, label, title, stretch, iconOnly, ...rest } = props
  const ariaLabel = getAriaLabel(
    label || title || (children as React.ReactNode | React.ReactNode[])
  )

  return {
    ref,
    ['aria-label']: ariaLabel,
    css: getButtonStyles({ stretch, iconOnly, ...rest }),
    title: title,
    'data-track-on': rest['data-track-on'] ?? 'click',
    'data-track-category': rest['data-track-category'] ?? 'general',
    'data-track-action': rest['data-track-action'] ?? kebabCase(ariaLabel),
    'data-track-label': rest['data-track-label'] ?? 'button',
    children: loading ? <Loader size={10} /> : children || label,
    ...omit(rest, [...Object.keys(BUTTON_TYPES)])
  }
}

interface ButtonPropAs extends ButtonProps {
  as?: React.ElementType
  // unless explicitly defined here, we are getting ts warnings on this prop being passed through to the component.
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
}

const Button = React.forwardRef<HTMLButtonElement, ButtonPropAs>(
  ({ as: Component = 'button', ...rest }, ref) => {
    return <Component {...getButtonProps(rest, ref)} />
  }
)

Button.displayName = 'Button'

export default Button

export const LinkButton = React.forwardRef<
  HTMLButtonElement,
  ButtonProps & {
    state?: LinkProps['state']
    to: LinkProps['to']
  }
>(({ state, to, ...rest }, ref) => {
  return <Link state={state} to={to} {...getButtonProps(rest, ref)} />
})

LinkButton.displayName = 'LinkButton'
